import { apolloClinicNewLink } from 'graphql/apollo';
import {
  MedicalServiceByCategoryIdInput,
  Pagination,
  useMedicalServicesByCategoryIdLazyQuery,
} from 'graphql/generated/graphql';
import { MedicalService } from 'model/graphql';
import React, { useEffect, useState } from 'react';

const PER_PAGE = 30;

interface IMedicalServiceContext {
  medicalServices?: MedicalService[];
  isLoading?: boolean;
  isEndOfTheList?: boolean;
  isInitialDataFetched?: boolean;
  totalCount?: number;
  fetchMedicalServices?: (input: MedicalServiceByCategoryIdInput, perPage?: number) => void;
  fetchMoreMedicalServices?: () => void;
}

const MedicalServiceContext = React.createContext<IMedicalServiceContext>({});

const MedicalServiceProvider = ({ children }: { children: React.ReactNode }) => {
  const [medicalServices, setMedicalServices] = useState<MedicalService[]>([]);
  const [isInitialDataFetched, setInitialDataFetched] = useState<boolean>();
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isEndOfTheList, setEndOfTheList] = useState(false);
  const [queryInput, setQueryInput] = useState<MedicalServiceByCategoryIdInput>(null);

  const [getMedicalServicesByCategoryId, { data, loading }] = useMedicalServicesByCategoryIdLazyQuery({
    client: apolloClinicNewLink,
  });

  useEffect(() => {
    if (loading && currentPage === 0) {
      setInitialDataFetched(false);
    } else if (!loading && isInitialDataFetched === false) {
      setInitialDataFetched(true);
    }
  }, [loading]);

  useEffect(() => {
    if (!data) return;
    setInitialDataFetched(true);
    if (currentPage === 0 || medicalServices?.length === 0) {
      setMedicalServices(data?.medicalServicesByCategoryId?.data);
    } else {
      const concatenatedMedicalServices = [...medicalServices, ...data?.medicalServicesByCategoryId?.data];
      setMedicalServices(concatenatedMedicalServices);
    }

    setEndOfTheList((data?.medicalServicesByCategoryId?.data?.length ?? 0) < PER_PAGE);

    setTotalCount(data?.medicalServicesByCategoryId?.totalCount);
  }, [data]);

  function fetchMedicalServicesByCategoryAtPage(
    page: number,
    input: MedicalServiceByCategoryIdInput,
    perPage: number = PER_PAGE
  ) {
    const pagination: Pagination = { skip: page * perPage, take: perPage };
    const variables = {
      input,
      pagination,
    };
    getMedicalServicesByCategoryId({ variables });
  }

  function fetchMedicalServices(input: MedicalServiceByCategoryIdInput, perPage: number = PER_PAGE) {
    const page = 0;
    setQueryInput(input);
    setCurrentPage(page);

    fetchMedicalServicesByCategoryAtPage(page, input, perPage);
  }

  function fetchMoreMedicalServices() {
    if (isEndOfTheList) return false;

    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);

    fetchMedicalServicesByCategoryAtPage(nextPage, queryInput);
  }

  return (
    <MedicalServiceContext.Provider
      value={{
        medicalServices,
        isLoading: loading,
        isEndOfTheList,
        totalCount,
        isInitialDataFetched,
        fetchMedicalServices,
        fetchMoreMedicalServices,
      }}
    >
      {children}
    </MedicalServiceContext.Provider>
  );
};

export { MedicalServiceContext, MedicalServiceProvider };
